/**
 * @file PresetLibraryDialog.tsx
 * @description «Добавить блок» — библиотека заготовок секций (phase19.16 Р2;
 * uiux-design-r2 §5, §10; SB26, SB27, SB64): окно 960×80vh на ПК, весь
 * экран на телефоне. Группы слева со счётчиками (на телефоне — чипы),
 * карточки-схемы в цветах сайта, поиск по названию и «для чего», место
 * вставки «Куда: после «…»», [Добавить выбранный] закреплён внизу, двойной
 * клик вставляет сразу. Прежних 14 типов здесь нет (решение 2026-10-09).
 */

import { SECTION_PRESETS, type SectionPreset, type SectionPresetGroup, type SiteColors } from "@chaiberi/shared";
import { useEffect, useMemo, useState, type CSSProperties } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../ui/dialog";
import { SearchInput } from "../../ui/SearchInput";
import { PresetCard } from "./PresetCard";
import { PresetPlacePicker, type PlaceBlock } from "./PresetPlacePicker";
import {
  filterPresets,
  groupPresets,
  PRESET_SEARCH_EMPTY,
  presetBlockedReason,
  type PresetFeatures,
} from "../../../lib/site-builder/preset-library";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  /** Место: id блока выше, `null` — в начало, `undefined` — в конец. */
  after: string | null | undefined;
  blocks: readonly PlaceBlock[];
  /** Цвета ролей черновика — схемы окрашиваются ими. */
  colors: SiteColors | null;
  features: PresetFeatures;
  presets?: readonly SectionPreset[];
  /** Группа первой в списке и выбранной сразу («Блог» — из панели «Блог»). */
  firstGroup?: SectionPresetGroup;
  /** Имя места блога для «Куда» (окно открыто на месте над/под лентой). */
  placeName?: string;
  onClose: () => void;
  /** Вставить; `true` — вставлено (окно закрывается). */
  onInsert: (preset: SectionPreset, after: string | null | undefined) => Promise<boolean>;
}

type GroupFilter = SectionPresetGroup | "all";

/** Переменные ролей для схем: `--site-color-<роль>`. */
function roleVars(colors: SiteColors | null): CSSProperties {
  if (!colors) return {};
  return Object.fromEntries(Object.entries(colors).map(([role, hex]) => [`--site-color-${role}`, hex])) as CSSProperties;
}

export function PresetLibraryDialog({ open, after, blocks, colors, features, presets = SECTION_PRESETS, firstGroup, placeName, onClose, onInsert }: Props) {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<GroupFilter>("all");
  const [place, setPlace] = useState(after);
  const [picked, setPicked] = useState<SectionPreset | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPlace(after);
    setPicked(null);
    setQuery("");
    setGroup(firstGroup ?? "all");
  }, [open, after, firstGroup]);

  const found = useMemo(() => filterPresets(presets, query), [presets, query]);
  const allGroups = useMemo(() => {
    const groups = groupPresets(found);
    return firstGroup ? [...groups.filter((g) => g.key === firstGroup), ...groups.filter((g) => g.key !== firstGroup)] : groups;
  }, [found, firstGroup]);
  const shown = group === "all" ? allGroups : allGroups.filter((g) => g.key === group);

  async function insert(preset: SectionPreset | null) {
    if (!preset || busy || presetBlockedReason(preset, features)) return;
    setBusy(true);
    try {
      if (await onInsert(preset, place)) onClose();
    } finally {
      setBusy(false);
    }
  }

  const chip = (key: GroupFilter, label: string, count: number) => (
    <button
      key={key}
      type="button"
      aria-pressed={group === key}
      onClick={() => setGroup(key)}
      className={cn(
        "flex min-h-12 shrink-0 items-center justify-between gap-3 rounded-lg px-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-h-10",
        group === key ? "bg-primary/10 font-medium text-on-surface" : "text-on-surface-variant hover:bg-surface-low",
      )}
    >
      <span className="type-body">{label}</span>
      <span className="type-caption tabular-nums">{count}</span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent fullScreen className="gap-3 p-4 sm:h-[80vh] sm:max-w-[960px] sm:p-6" style={roleVars(colors)}>
        <div className="flex shrink-0 flex-col gap-2 pr-10 sm:flex-row sm:items-center">
          <DialogTitle>Добавить блок</DialogTitle>
          <div className="sm:ml-auto sm:w-80">
            <SearchInput value={query} onChange={setQuery} placeholder="Найти: «отзывы», «кнопка»…" ariaLabel="Поиск заготовки" />
          </div>
        </div>
        <DialogDescription render={<div />}>
          <PresetPlacePicker after={place} blocks={blocks} onChange={setPlace} placeName={placeName} />
        </DialogDescription>
        <div className="flex min-h-0 flex-1 flex-col gap-3 sm:flex-row">
          <nav aria-label="Группы заготовок" className="flex shrink-0 gap-1 overflow-x-auto pb-1 sm:w-48 sm:flex-col sm:overflow-y-auto">
            {chip("all", "Все", found.length)}
            {allGroups.map((g) => chip(g.key, g.label, g.presets.length))}
          </nav>
          <div role="radiogroup" aria-label="Заготовки" className="min-h-0 flex-1 overflow-y-auto pb-2">
            {shown.length === 0 ? <p className="type-body p-4 text-on-surface-variant">{PRESET_SEARCH_EMPTY}</p> : null}
            {shown.map((g) => (
              <section key={g.key} aria-label={g.label} className="mb-4 flex flex-col gap-2">
                <h3 className="type-caption font-medium text-on-surface-variant">{g.label}</h3>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {g.presets.map((preset) => (
                    <PresetCard
                      key={preset.key}
                      preset={preset}
                      selected={picked?.key === preset.key}
                      blockedReason={presetBlockedReason(preset, features)}
                      onSelect={() => setPicked(preset)}
                      onInsert={() => void insert(preset)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
        <div className="flex shrink-0 gap-2 bg-surface-lowest pt-2 pb-[env(safe-area-inset-bottom)] sm:justify-end">
          <Button variant="outline" className="max-sm:h-12 max-sm:flex-1" onClick={onClose}>
            Отмена
          </Button>
          <Button className="max-sm:h-12 max-sm:flex-1" disabled={!picked} loading={busy} onClick={() => void insert(picked)}>
            Добавить выбранный
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
